import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLabText } from "../lib/labText";
import { ParticipationBox } from "../components/lab/ParticipationBox";
import { ApplyDialog } from "../components/lab/ApplyDialog";
import { ReleaseSection } from "../components/lab/ReleaseSection";
import { ExperimentCard } from "../components/lab/ExperimentCard";
import { LabCheckout } from "../components/lab/LabCheckout";
import { EvaluationDialog } from "../components/lab/EvaluationDialog";

const Intro = ({ s, pick, eyebrow }) => (
  <motion.header initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl">
    <p className="text-[11px] uppercase tracking-[0.35em] text-[#C49A6C]">{eyebrow}</p>
    <h1 className="serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5" data-testid="lab-title">{pick(s, "title")}</h1>
    <div className="mt-10 space-y-5 text-[#B8B0A4] leading-[1.9] text-[15px] md:text-base">
      {pick(s, "intro").split(/\n\s*\n/).map((para, i) => <p key={`${i}-${para.slice(0, 24)}`} className={i === 0 ? "serif text-2xl text-[#F2EBE1] leading-snug" : ""}>{para}</p>)}
    </div>
    <p className="serif italic text-2xl md:text-3xl text-[#E7CBA9] mt-12 border-s-2 border-[#C49A6C] ps-6" data-testid="lab-statement">{pick(s, "statement")}</p>
  </motion.header>
);

const Notice = ({ title, text, testid }) => (
  <div className="border border-[#262421] p-5" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-[0.3em] text-[#8A857D]">{title}</p>
    <p className="text-sm text-[#A39A8D] mt-2 leading-relaxed whitespace-pre-line">{text}</p>
  </div>
);

export default function Experiments() {
  const { t, pick } = useLabText();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [applying, setApplying] = useState(false);
  const [buy, setBuy] = useState(null);
  const [evalExp, setEvalExp] = useState(null);
  const load = useCallback(() => api.get("/lab").then((r) => setData(r.data)).catch(() => setData({ error: true })), []);
  useEffect(() => { load(); }, [load, user]);
  if (!data) return <div className="min-h-[60vh]" />;
  if (data.error) return <div className="max-w-xl mx-auto py-32 text-center text-[#8A857D]">—</div>;
  const s = data.settings;
  const participant = data.me?.participant;
  const approved = participant?.status === "approved";
  const paid = params.get("paid");
  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 space-y-16" data-testid="lab-page">
      <Intro s={s} pick={pick} eyebrow={t("eyebrow")} />
      {paid && <p className="flex items-center gap-3 border border-[#2F4A2C] bg-[#0F1A0E] p-4 text-sm text-[#CFE3CB]" data-testid="lab-paid-banner"><CheckCircle2 size={18} />{t("paid_thanks")} <span dir="ltr">{paid}</span></p>}
      <div className="grid md:grid-cols-2 gap-5">
        <Notice title={t("safety")} text={pick(s, "disclaimer")} testid="lab-disclaimer" />
        <Notice title={t("ip_title")} text={pick(s, "ip")} testid="lab-ip" />
      </div>
      <ParticipationBox user={user} participant={participant} onRequest={() => setApplying(true)} />
      <div className="space-y-16">
        <h2 className="text-[11px] uppercase tracking-[0.35em] text-[#8A857D]">{t("releases")}</h2>
        {!data.releases.length && <p className="text-[#8A857D]" data-testid="lab-no-releases">{t("no_releases")}</p>}
        {data.releases.map((r) => (
          <ReleaseSection key={r.id} r={r} approved={approved} me={data.me} voices={data.published_evaluations} onBuy={(rel, mode) => setBuy({ rel, mode })} onEvaluate={setEvalExp} stages={s} />
        ))}
      </div>
      {data.archive.length > 0 && (
        <section className="border-t border-[#262421] pt-12" data-testid="lab-archive">
          <h2 className="serif text-3xl md:text-4xl mb-8">{t("archive")}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {data.archive.map((e) => <ExperimentCard key={e.id} e={e} purchased={(data.me?.purchased || []).includes(e.id)} evalState={data.me?.evaluations?.[e.id]} onEvaluate={setEvalExp} voices={data.published_evaluations[e.id]} stages={s} />)}
          </div>
        </section>
      )}
      {buy && approved && <LabCheckout release={buy.rel} mode={buy.mode} participant={participant} settings={s} user={user} onClose={() => setBuy(null)} />}
      {applying && <ApplyDialog user={user} questions={data.application_questions} onClose={() => setApplying(false)} onDone={() => { setApplying(false); load(); }} />}
      {evalExp && <EvaluationDialog exp={evalExp} questions={data.questions} settings={s} state={data.me?.evaluations?.[evalExp.id]} onClose={() => setEvalExp(null)} onDone={() => { setEvalExp(null); load(); }} />}
    </div>
  );
}
